import { supabase } from '@/integrations/supabase/client';

export interface BriefBulletsEuropaTask {
  id: string;
  task_type: string;
  schedule_time: string;
  last_run: string | null;
  next_run: string | null;
  status: 'active' | 'paused' | 'failed';
  config: {
    retry_time?: string;
    max_retries?: number;
    notification_email?: string;
    photo_url?: string;
    day_of_week?: number;
  };
  error_message: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export interface TaskLog {
  id: string;
  task_id: string;
  task_type: string;
  status: 'success' | 'failed' | 'running';
  message: string | null;
  metadata: any;
  execution_time_ms: number | null;
  created_at: string;
}

export interface BriefBulletsEuropaArticle {
  id: string;
  title: string;
  created_at: string;
  publication_status: string;
  article_type: string;
}

export class BriefBulletsEuropaService {
  static async getTaskStatus(): Promise<BriefBulletsEuropaTask | null> {
    const { data, error } = await supabase
      .from('scheduled_tasks')
      .select('*')
      .eq('task_type', 'briefr_bullets_europa')
      .single();

    if (error) {
      console.error('Error fetching task status:', error);
      return null;
    }

    return data as BriefBulletsEuropaTask;
  }

  static async getThisWeekStatus(): Promise<{
    published: boolean;
    article?: BriefBulletsEuropaArticle;
  }> {
    // Find Monday this week
    const mondayThisWeek = new Date();
    mondayThisWeek.setHours(0, 0, 0, 0);
    const daysSinceMonday = (mondayThisWeek.getDay() + 6) % 7;
    mondayThisWeek.setDate(mondayThisWeek.getDate() - daysSinceMonday);

    const { data, error } = await supabase
      .from('interviews')
      .select('id, title, created_at, publication_status, article_type')
      .eq('article_type', 'brief_bullets_europa' as any)
      .eq('title', 'Briefr Bullets: Hva skjer i Europa denne uken?')
      .gte('created_at', mondayThisWeek.toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error checking week status:', error);
      return { published: false };
    }

    return {
      published: !!data,
      article: data ? (data as BriefBulletsEuropaArticle) : undefined,
    };
  }

  static async triggerManualPublish(): Promise<{
    success: boolean;
    articleId?: string;
    error?: string;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke('publish-briefr-bullets-europa', {
        body: {},
      });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      return {
        success: data.success,
        articleId: data.articleId,
        error: data.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  static async getPublishHistory(weeks: number = 4): Promise<BriefBulletsEuropaArticle[]> {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - (weeks * 7));
    startDate.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from('interviews')
      .select('id, title, created_at, publication_status, article_type')
      .eq('article_type', 'brief_bullets_europa' as any)
      .eq('title', 'Briefr Bullets: Hva skjer i Europa denne uken?')
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching history:', error);
      return [];
    }

    return data as BriefBulletsEuropaArticle[];
  }

  static async getTaskLogs(limit: number = 10): Promise<TaskLog[]> {
    const { data, error } = await supabase
      .from('task_logs')
      .select('*')
      .eq('task_type', 'briefr_bullets_europa')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching task logs:', error);
      return [];
    }

    return data as TaskLog[];
  }

  static async toggleAutomaticPublishing(enable: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('scheduled_tasks')
        .update({
          status: enable ? 'active' : 'paused',
        })
        .eq('task_type', 'briefr_bullets_europa');

      if (error) {
        console.error('Error toggling automatic publishing:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error toggling automatic publishing:', error);
      return false;
    }
  }

  static async deleteThisWeekArticle(): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const weekStatus = await this.getThisWeekStatus();
      
      if (!weekStatus.published || !weekStatus.article) {
        return {
          success: false,
          error: 'Ingen artikkel funnet for denne uken',
        };
      }

      const { error: updateError } = await supabase
        .from('interviews')
        .update({
          is_in_feeder: false,
          publication_status: 'pending',
        })
        .eq('id', weekStatus.article.id);

      if (updateError) {
        return {
          success: false,
          error: updateError.message,
        };
      }

      const { error: deleteError } = await supabase
        .from('interviews')
        .delete()
        .eq('id', weekStatus.article.id);

      if (deleteError) {
        return {
          success: false,
          error: deleteError.message,
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}
